<template>
  <modal
    :name="modalName"
    :width="'98%'"
    :height="'96%'"
    :max-width="1760"
    :adaptive="true"
    :scrollable="false"
    class="support-ticket-modal modal-not-overflow"
    @closed="$emit('close')"
  >
    <section class="ticket-workspace" aria-labelledby="ticket-workspace-title">
      <ticket-modal-header
        :ticket="ticket"
        :can-edit="canEditSupport"
        :can-toggle-important="canEditSupport"
        :working="working"
        @edit-title="openTitleEdit"
        @edit-requester="openRequesterEdit"
        @toggle-important="toggleImportant"
        @close="close"
      />
      <div v-if="loading" class="ticket-workspace__loading"><app-spinner design="new" /></div>
      <div v-else-if="error && !ticket" class="ticket-workspace__fatal" role="alert">
        <strong>Unable to load this ticket</strong><p>{{ error }}</p><app-button design="new" @click.native="load">Retry</app-button>
      </div>
      <template v-else-if="ticket">
        <div class="ticket-workspace__toolbar">
          <div class="ticket-workspace__tag-bar">
            <app-status-badge v-if="ticket.waiting_for" design="new" tone="amber" :label="`Waiting for ${waitingLabel(ticket.waiting_for)}`" />
            <ticket-tag-controls
              v-if="canEditSupport"
              :selected-tags="ticket.tags"
              :selected-labels="ticket.responsible_labels"
              :tags="tags"
              :labels="labels"
              :working="working"
              @update-tags="updateTags"
              @update-labels="updateResponsibleLabels"
              @dictionary-created="dictionaryCreated"
              @dictionary-updated="dictionaryUpdated"
            />
          </div>
          <div>
            <app-button v-if="isInternal" class="ticket-workspace__tools-button" design="new" size="small" @click.native="toolsOpen = true">Checklist & timeline</app-button>
            <app-button v-if="!isInternal" class="ticket-workspace__details-button" design="new" size="small" @click.native="detailsOpen = true">Details</app-button>
            <app-button v-if="isInternal && ticket.status !== 'closed'" design="new" size="small" theme="dark" @click.native="markUnread">Mark unread</app-button>
            <app-button v-if="canEditSupport && ticket.status !== 'closed'" design="new" size="small" theme="error" :disabled="!canClose" :title="!canClose ? 'Take this ticket into work before closing it.' : ''" @click.native="$modal.show('support-ticket-close')">Close ticket</app-button>
            <app-button v-if="ticket.status === 'closed'" design="new" size="small" theme="success" @click.native="$modal.show('support-ticket-reopen')">Reopen</app-button>
          </div>
        </div>

        <div v-if="canTake" class="ticket-workspace__take" role="status">
          <div>
            <span>Unassigned request</span>
            <strong>Waiting for {{ openAgeLabel }}</strong>
            <small>Take ownership to update its status or close it.</small>
          </div>
          <app-button design="new" theme="success" :disabled="working" @click.native="takeIntoWork">Take into work</app-button>
        </div>
        <p v-if="error" class="ticket-workspace__error" role="alert">{{ error }}</p>

        <div class="ticket-workspace__body" :class="{ 'ticket-workspace__body--employee': !isInternal }">
          <button v-if="detailsOpen || toolsOpen" type="button" class="ticket-workspace__scrim" aria-label="Close side panel" @click="closeSidePanels"></button>
          <section v-if="isInternal" class="ticket-workspace__operations" :class="{ 'ticket-workspace__operations--open': toolsOpen }">
            <div class="ticket-workspace__overview">
              <section class="ticket-summary-card">
                <header><h3>Status & owner</h3><button v-if="canEditSupport" type="button" aria-label="Edit status and owner" :disabled="working" @click="openWorkflowEdit"><img src="@/assets/img/edit-table-icon.svg" alt=""></button></header>
                <dl>
                  <div><dt>Status</dt><dd><app-status-badge design="new" compact :label="statusLabel(ticket.status)" :tone="statusTone(ticket.status)" /></dd></div>
                  <div><dt>Assignee</dt><dd class="ticket-summary-card__owner"><img v-if="assigneeAvatar" :src="assigneeAvatar" alt=""><span v-else aria-hidden="true">{{ assigneeInitials }}</span><strong>{{ assigneeName }}</strong></dd></div>
                  <div v-if="sourcePageHref">
                    <dt>Source page</dt>
                    <dd class="ticket-summary-card__source-actions">
                      <span class="ticket-summary-card__source">
                        <a :href="sourcePageHref" target="_blank" rel="noopener" :title="`Open ${sourcePagePath}`">{{ sourcePageLabel }}</a>
                        <small>{{ sourcePagePath }}</small>
                      </span>
                      <button type="button" class="ticket-summary-card__copy-source" :aria-label="`Copy source path ${sourcePagePath}`" :title="`Copy ${sourcePagePath}`" @click="copySourcePath">
                        <img src="@/assets/img/link-blue-icon.svg" alt="">
                      </button>
                    </dd>
                  </div>
                  <div v-if="ticket.waiting_for"><dt>Waiting for</dt><dd>{{ waitingLabel(ticket.waiting_for) }}</dd></div>
                  <div v-if="ticket.resolution"><dt>Outcome</dt><dd>{{ resolutionLabel(ticket.resolution) }}</dd></div>
                </dl>
              </section>
              <section class="ticket-summary-card">
                <header><h3>Classification</h3><button v-if="canEditSupport" type="button" aria-label="Edit classification" :disabled="working" @click="openClassificationEdit"><img src="@/assets/img/edit-table-icon.svg" alt=""></button></header>
                <dl>
                  <div><dt>System</dt><dd><app-status-badge v-if="ticket.system" design="new" compact :label="ticket.system.name" :color="ticket.system.color" /><span v-else class="ticket-summary-card__empty">—</span></dd></div>
                  <div><dt>Direction</dt><dd><app-status-badge v-if="ticket.direction" design="new" compact :label="ticket.direction.name" :color="ticket.direction.color" /><span v-else class="ticket-summary-card__empty">—</span></dd></div>
                  <div><dt>Module</dt><dd><app-status-badge v-if="ticket.module" design="new" compact :label="ticket.module.name" :color="ticket.module.color" /><span v-else class="ticket-summary-card__empty">—</span></dd></div>
                  <div><dt>Priority</dt><dd><ticket-priority-indicator :priority="ticket.priority" show-label /></dd></div>
                </dl>
              </section>
            </div>

            <aside class="ticket-workspace__tools" :class="{ 'ticket-workspace__tools--open': toolsOpen }">
              <header class="ticket-workspace__panel-header"><strong>Checklist & timeline</strong><button type="button" aria-label="Close work panel" @click="toolsOpen = false">×</button></header>
              <ticket-checklist-panel
                :items="ticket.checklist_items"
                :readonly="!canEditSupport"
                :new-item="newChecklist"
                :working="working"
                @update:new-item="newChecklist = $event"
                @add="addChecklist"
                @toggle="toggleChecklist"
                @remove="removeChecklist"
                @reorder="reorderChecklist"
              />
              <ticket-timeline :ticket-id="ticket.id" :refresh-key="timelineRefreshKey" />
            </aside>

            <aside class="ticket-workspace__notes">
              <section class="ticket-workspace__work-panel ticket-workspace__work-panel--notes">
                <header><strong>Internal notes</strong><span>{{ ticket.notes.length }}</span></header>
                <div class="ticket-workspace__work-content ticket-workspace__work-content--notes">
                  <p>Never visible to the requester.</p>
                  <div v-if="ticket.notes.length" class="ticket-notes">
                    <article v-for="note in ticket.notes" :key="note.id"><header><strong>{{ note.author ? note.author.full_name : 'Former user' }}</strong><time>{{ formatDateTime(note.created_at) }}</time></header><p>{{ note.body }}</p><support-file-preview-list v-if="note.attachments.length" message :files="note.attachments" @remove="deleteAttachment($event.file)" /></article>
                  </div>
                  <div v-if="canEditSupport" class="ticket-note__composer"><app-textarea v-model="newNote" design="new" placeholder="Add investigation context or a private hand-off…" @paste.native="pasteNoteFiles" /><app-button design="new" size="small" :disabled="!newNote || working" @click.native="addNote">Add internal note</app-button></div>
                </div>
              </section>
              <section v-if="canEditSupport" class="ticket-workspace__attachments-panel">
                <header><div><strong>Attachments</strong><span>For the next internal note</span></div><label class="ticket-note__attach"><input type="file" multiple accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.csv" @change="selectNoteFiles"><i>+ Add files</i></label></header>
                <support-file-preview-list v-if="newNoteFiles.length" queued :files="newNoteFiles" @remove="newNoteFiles.splice($event.index, 1)" />
              </section>
            </aside>
          </section>

          <ticket-conversation-panel
            ref="conversation"
            :messages="messages"
            :message-count="messageCount"
            :requester-id="ticket.requester && ticket.requester.id"
            :has-older-messages="hasOlderMessages"
            :loading-messages="loadingMessages"
            :new-message="newMessage"
            :files="messageFiles"
            :closed="ticket.status === 'closed'"
            :working="working"
            @load-older="loadOlderMessages"
            @update:new-message="newMessage = $event"
            @select-files="selectMessageFiles"
            @remove-file="messageFiles.splice($event, 1)"
            @delete-attachment="deleteAttachment"
            @send="sendMessage"
          />

          <aside v-if="!isInternal" class="ticket-workspace__details" :class="{ 'ticket-workspace__details--open': detailsOpen }">
            <header class="ticket-workspace__panel-header"><strong>Ticket details</strong><button type="button" aria-label="Close details" @click="detailsOpen = false">×</button></header>
            <section class="ticket-summary-card">
              <header><h3>Status & owner</h3><button v-if="canEditSupport" type="button" aria-label="Edit status and owner" :disabled="working" @click="openWorkflowEdit"><img src="@/assets/img/edit-table-icon.svg" alt=""></button></header>
              <dl>
                <div><dt>Status</dt><dd><app-status-badge design="new" compact :label="statusLabel(ticket.status)" :tone="statusTone(ticket.status)" /></dd></div>
                <div>
                  <dt>Assignee</dt>
                  <dd class="ticket-summary-card__owner">
                    <img v-if="assigneeAvatar" :src="assigneeAvatar" alt="">
                    <span v-else aria-hidden="true">{{ assigneeInitials }}</span>
                    <strong>{{ assigneeName }}</strong>
                  </dd>
                </div>
                <div v-if="ticket.waiting_for"><dt>Waiting for</dt><dd>{{ waitingLabel(ticket.waiting_for) }}</dd></div>
                <div v-if="ticket.resolution"><dt>Outcome</dt><dd>{{ resolutionLabel(ticket.resolution) }}</dd></div>
              </dl>
            </section>

            <section v-if="isInternal" class="ticket-summary-card">
              <header><h3>Classification</h3><button v-if="canEditSupport" type="button" aria-label="Edit classification" :disabled="working" @click="openClassificationEdit"><img src="@/assets/img/edit-table-icon.svg" alt=""></button></header>
              <dl>
                <div><dt>System</dt><dd><app-status-badge v-if="ticket.system" design="new" compact :label="ticket.system.name" :color="ticket.system.color" /><span v-else class="ticket-summary-card__empty">—</span></dd></div>
                <div><dt>Direction</dt><dd><app-status-badge v-if="ticket.direction" design="new" compact :label="ticket.direction.name" :color="ticket.direction.color" /><span v-else class="ticket-summary-card__empty">—</span></dd></div>
                <div><dt>Module</dt><dd><app-status-badge v-if="ticket.module" design="new" compact :label="ticket.module.name" :color="ticket.module.color" /><span v-else class="ticket-summary-card__empty">—</span></dd></div>
              </dl>
            </section>

          </aside>
        </div>
      </template>
    </section>

    <modal name="support-ticket-title-edit" :width="520" height="auto" :adaptive="true" class="modal-not-overflow">
      <section class="ticket-action-dialog">
        <app-modal-header design="new" title="Edit ticket title" @close="$modal.hide('support-ticket-title-edit')" />
        <form @submit.prevent="saveTitleEdit">
          <app-form-group design="new" label="Title" required>
            <app-input v-model="titleEdit" design="new" maxlength="160" :disabled="working" />
          </app-form-group>
          <p v-if="actionError" role="alert">{{ actionError }}</p>
          <footer><app-button design="new" theme="dark" type="button" @click.native="$modal.hide('support-ticket-title-edit')">Cancel</app-button><app-button design="new" theme="success" type="submit" :disabled="working || !canSaveTitleEdit">Save title</app-button></footer>
        </form>
      </section>
    </modal>

    <modal name="support-ticket-requester-edit" :width="520" height="auto" :adaptive="true" class="modal-not-overflow">
      <section class="ticket-action-dialog">
        <app-modal-header design="new" title="Edit requester" @close="$modal.hide('support-ticket-requester-edit')" />
        <form @submit.prevent="saveRequesterEdit">
          <app-form-group design="new" label="Requester" required>
            <app-select
              v-model="requesterEdit"
              design="new"
              :options="requesters"
              label="full_name"
              placeholder="Search an active Board user"
              :disabled="working"
              @search="searchRequesters"
            />
          </app-form-group>
          <p v-if="actionError" role="alert">{{ actionError }}</p>
          <footer><app-button design="new" theme="dark" type="button" @click.native="$modal.hide('support-ticket-requester-edit')">Cancel</app-button><app-button design="new" theme="success" type="submit" :disabled="working || !canSaveRequesterEdit">Save requester</app-button></footer>
        </form>
      </section>
    </modal>

    <modal name="support-ticket-workflow-edit" :width="460" height="auto" :adaptive="true" class="modal-not-overflow">
      <section class="ticket-action-dialog">
        <app-modal-header design="new" title="Edit status & owner" @close="$modal.hide('support-ticket-workflow-edit')" />
        <form @submit.prevent="saveWorkflowEdit">
          <app-form-group design="new" label="Status">
            <app-select v-model="workflowEdit.status" design="new" :options="activeStatuses" label="label" :reduce="option => option.value" />
          </app-form-group>
          <app-form-group v-if="workflowEdit.status === 'waiting'" design="new" label="Waiting for" required>
            <app-select v-model="workflowEdit.waiting_for" design="new" :options="waitingOptions" label="label" :reduce="option => option.value" />
          </app-form-group>
          <app-form-group design="new" label="Assignee">
            <app-select v-model="workflowEdit.assignee" design="new" :options="assignees" label="full_name" placeholder="Unassigned" />
          </app-form-group>
          <p v-if="actionError" role="alert">{{ actionError }}</p>
          <footer><app-button design="new" theme="dark" type="button" @click.native="$modal.hide('support-ticket-workflow-edit')">Cancel</app-button><app-button design="new" theme="success" type="submit" :disabled="working || !canSaveWorkflowEdit">Save changes</app-button></footer>
        </form>
      </section>
    </modal>

    <modal name="support-ticket-classification-edit" :width="520" height="auto" :adaptive="true" class="modal-not-overflow">
      <section class="ticket-action-dialog">
        <app-modal-header design="new" title="Edit classification" @close="$modal.hide('support-ticket-classification-edit')" />
        <form @submit.prevent="saveClassificationEdit">
          <dictionary-field :value="classificationEdit.system" kind="systems" label="System" singular="System" :options="systems" :disabled="working" can-create @input="changeClassificationSystem" @created="systems.push($event)" />
          <dictionary-field :value="classificationEdit.direction" kind="directions" label="Direction" singular="Direction" :options="directions" :disabled="working" :can-create="Boolean(classificationEdit.system)" :parent-payload="{ system: classificationEdit.system && classificationEdit.system.id }" @input="changeClassificationDirection" @created="directions.push($event)" />
          <dictionary-field :value="classificationEdit.module" kind="modules" label="Module" singular="Module" :options="modules" :disabled="working" :can-create="Boolean(classificationEdit.direction)" :parent-payload="{ direction: classificationEdit.direction && classificationEdit.direction.id }" @input="changeClassificationModule" @created="modules.push($event)" />
          <app-form-group design="new" label="Priority">
            <app-select v-model="classificationEdit.priority" design="new" :options="priorityOptions" label="label" :reduce="option => option.value" />
          </app-form-group>
          <p v-if="actionError" role="alert">{{ actionError }}</p>
          <footer><app-button design="new" theme="dark" type="button" @click.native="$modal.hide('support-ticket-classification-edit')">Cancel</app-button><app-button design="new" theme="success" type="submit" :disabled="working || !classificationChanged">Save changes</app-button></footer>
        </form>
      </section>
    </modal>

    <modal name="support-ticket-close" :width="560" height="auto" :adaptive="true" class="modal-not-overflow">
      <section class="ticket-action-dialog">
        <app-modal-header design="new" title="Close ticket" @close="$modal.hide('support-ticket-close')" />
        <form @submit.prevent="closeTicket">
          <app-form-group design="new" label="Resolution" required><app-select v-model="closeForm.resolution" design="new" :options="resolutionOptions" label="label" :reduce="option => option.value" /></app-form-group>
          <app-form-group design="new" label="Public outcome" required><app-textarea v-model="closeForm.public_summary" design="new" placeholder="Explain the outcome in language the requester can understand." /></app-form-group>
          <app-form-group v-if="needsInternalReason" design="new" label="Internal reason" required><app-textarea v-model="closeForm.internal_reason" design="new" placeholder="Record the technical or prioritization reason." /></app-form-group>
          <app-form-group v-if="closeForm.resolution === 'duplicate'" design="new" label="Original ticket" required><app-select v-model="closeForm.duplicate_of" design="new" :options="duplicateOptions" label="subject" /></app-form-group>
          <p v-if="actionError" role="alert">{{ actionError }}</p>
          <footer><app-button design="new" theme="dark" type="button" @click.native="$modal.hide('support-ticket-close')">Cancel</app-button><app-button design="new" theme="error" type="submit" :disabled="working || !canCloseTicket">Close ticket</app-button></footer>
        </form>
      </section>
    </modal>

    <modal name="support-ticket-reopen" :width="520" height="auto" :adaptive="true" class="modal-not-overflow">
      <section class="ticket-action-dialog">
        <app-modal-header design="new" title="Reopen ticket" @close="$modal.hide('support-ticket-reopen')" />
        <form @submit.prevent="reopenTicket">
          <app-form-group design="new" label="Why is this ticket being reopened?" required><app-textarea v-model="reopenReason" design="new" /></app-form-group>
          <p v-if="actionError" role="alert">{{ actionError }}</p>
          <footer><app-button design="new" theme="dark" type="button" @click.native="$modal.hide('support-ticket-reopen')">Cancel</app-button><app-button design="new" theme="success" type="submit" :disabled="working || !reopenReason">Reopen</app-button></footer>
        </form>
      </section>
    </modal>
  </modal>
</template>

<script>
import { techSupportKey } from '@/utils/techSupportAccess'
import {
  deleteSupportTicketAttachment,
  deleteSupportTicketChecklistItem,
  getSupportTicket,
  getSupportTicketDictionary,
  getSupportTicketMessages,
  getSupportTickets,
  getSupportTicketUsers,
  patchSupportTicket,
  patchSupportTicketChecklistItem,
  postSupportTicketChecklist,
  postSupportTicketChecklistReorder,
  postSupportTicketClose,
  postSupportTicketMarkUnread,
  postSupportTicketMessage,
  postSupportTicketNote,
  postSupportTicketReopen,
  postSupportTicketTake,
  postSupportTicketTransition
} from '@/http'
import DictionaryField from './DictionaryField'
import TicketChecklistPanel from './TicketChecklistPanel'
import TicketConversationPanel from './TicketConversationPanel'
import TicketModalHeader from './TicketModalHeader'
import TicketPriorityIndicator from './TicketPriorityIndicator'
import SupportFilePreviewList from './SupportFilePreviewList'
import TicketTagControls from './TicketTagControls'
import TicketTimeline from './TicketTimeline'
import {
  apiError, formatDateTime, idempotencyKey, isStaleTicketError, normalizePage,
  PRIORITY_OPTIONS, RESOLUTION_OPTIONS, resolutionLabel, STATUS_OPTIONS, statusLabel, statusTone,
  WAITING_OPTIONS, waitingLabel
} from '../ticketState'
import { appendSupportFiles, clipboardSupportFiles, uploadSupportFiles } from '../supportFileUploads'

export default {
  name: 'SupportTicketModal',
  components: { DictionaryField, SupportFilePreviewList, TicketChecklistPanel, TicketConversationPanel, TicketModalHeader, TicketPriorityIndicator, TicketTagControls, TicketTimeline },
  props: {
    ticketId: { type: Number, required: true },
    employeeMode: Boolean
  },
  data() {
    return {
      ticket: null,
      messages: [],
      messageCount: 0,
      messagePage: 1,
      hasOlderMessages: false,
      loading: true,
      loadingMessages: false,
      working: false,
      error: '',
      actionError: '',
      newMessage: '',
      messageFiles: [],
      newNote: '',
      newNoteFiles: [],
      newChecklist: '',
      systems: [],
      directions: [],
      modules: [],
      tags: [],
      labels: [],
      assignees: [],
      requesters: [],
      draft: { status: null, waiting_for: null, assignee: null, priority: 'medium', is_important: false, system: null, direction: null, module: null, tags: [], responsible_labels: [] },
      titleEdit: '',
      requesterEdit: null,
      workflowEdit: { status: null, waiting_for: null, assignee: null },
      classificationEdit: { system: null, direction: null, module: null, priority: 'medium' },
      timelineRefreshKey: 0,
      detailsOpen: false,
      toolsOpen: false,
      closeForm: { resolution: null, public_summary: '', internal_reason: '', duplicate_of: null },
      duplicateOptions: [],
      reopenReason: '',
      now: Date.now(),
      ageInterval: null,
      activeStatuses: STATUS_OPTIONS.filter(option => option.value !== 'closed'),
      priorityOptions: PRIORITY_OPTIONS,
      waitingOptions: WAITING_OPTIONS,
      resolutionOptions: RESOLUTION_OPTIONS
    }
  },
  computed: {
    modalName() { return `support-ticket-workspace-${this.ticketId}` },
    currentUser() { return this.$store.state.userData || {} },
    groupCodes() { return this.$store.state.userGroups.map(group => group.code) },
    isInternal() { return !this.employeeMode && this.$store.getters['permissions/can'](techSupportKey('support')) },
    canEditSupport() { return this.isInternal && this.$store.getters['permissions/can'](techSupportKey('support', 'edit')) },
    isOwnedByMe() { return Boolean(this.ticket?.assignee && this.ticket.assignee.id === this.currentUser.id) },
    canClose() { return Boolean(this.canEditSupport && this.ticket?.assignee && (this.isOwnedByMe || this.groupCodes.includes('general_manager'))) },
    canTake() { return this.canEditSupport && !this.ticket?.assignee && ['new', 'in_progress'].includes(this.ticket?.status) },
    openAgeLabel() {
      const seconds = Math.max(0, Math.floor((this.now - new Date(this.ticket?.created_at).getTime()) / 1000))
      if (seconds < 60) return `${seconds} seconds`
      if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes`
      if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours`
      return `${Math.floor(seconds / 86400)} days`
    },
    assigneeName() { return this.ticket?.assignee?.full_name || 'Unassigned' },
    assigneeAvatar() { return this.ticket?.assignee?.crop?.crop_url || '' },
    assigneeInitials() { return this.assigneeName.split(' ').filter(Boolean).slice(0, 2).map(part => part.charAt(0).toUpperCase()).join('') || '—' },
    sourcePagePath() {
      const path = this.ticket?.page_context?.path
      return typeof path === 'string' && path.startsWith('/') && !path.startsWith('//') ? path : ''
    },
    sourcePageHref() { return this.sourcePagePath },
    sourcePageLabel() {
      const context = this.ticket?.page_context || {}
      return context.page_title || context.route_name || this.sourcePagePath
    },
    workflowStatusChanged() {
      return this.workflowEdit.status !== this.ticket?.status
        || this.workflowEdit.waiting_for !== this.ticket?.waiting_for
    },
    workflowAssigneeChanged() { return (this.workflowEdit.assignee?.id || null) !== (this.ticket?.assignee?.id || null) },
    workflowMetadataChanged() { return this.workflowAssigneeChanged },
    canSaveWorkflowEdit() {
      if (!this.workflowStatusChanged && !this.workflowMetadataChanged) return false
      return this.workflowEdit.status !== 'waiting' || Boolean(this.workflowEdit.waiting_for)
    },
    classificationChanged() {
      return (this.classificationEdit.system?.id || null) !== (this.ticket?.system?.id || null)
        || (this.classificationEdit.direction?.id || null) !== (this.ticket?.direction?.id || null)
        || (this.classificationEdit.module?.id || null) !== (this.ticket?.module?.id || null)
        || this.classificationEdit.priority !== (this.ticket?.priority || 'medium')
    },
    canSaveTitleEdit() {
      const title = this.titleEdit.trim()
      return Boolean(title && title.length <= 160 && title !== this.ticket?.subject)
    },
    canSaveRequesterEdit() {
      return Boolean(this.requesterEdit && this.requesterEdit.id !== this.ticket?.requester?.id)
    },
    needsInternalReason() { return ['deferred', 'cannot_resolve', 'not_reproduced'].includes(this.closeForm.resolution) },
    canCloseTicket() {
      if (!this.closeForm.resolution || !this.closeForm.public_summary.trim()) return false
      if (this.needsInternalReason && !this.closeForm.internal_reason.trim()) return false
      return this.closeForm.resolution !== 'duplicate' || Boolean(this.closeForm.duplicate_of)
    }
  },
  watch: {
    'closeForm.resolution'(value) {
      if (value !== 'duplicate' || this.duplicateOptions.length) return
      getSupportTickets({ status: 'closed', page_size: 100 }).then(response => {
        this.duplicateOptions = normalizePage(response.data).filter(item => item.id !== this.ticket.id)
      })
    }
  },
  mounted() {
    this.$modal.show(this.modalName)
    this.load()
    this.ageInterval = window.setInterval(() => { this.now = Date.now() }, 30000)
  },
  beforeDestroy() {
    window.clearInterval(this.ageInterval)
    this.$modal.hide(this.modalName)
  },
  methods: {
    apiError,
    formatDateTime,
    resolutionLabel,
    statusLabel,
    statusTone,
    waitingLabel,
    copySourcePath() {
      return navigator.clipboard.writeText(this.sourcePagePath).then(() => {
        this.$notify({ type: 'success', title: 'Path copied to clipboard' })
      }).catch(() => {
        this.$notify({ type: 'error', title: 'Unable to copy path' })
      })
    },
    openTitleEdit() {
      if (!this.canEditSupport || this.working) return
      this.titleEdit = this.ticket.subject
      this.actionError = ''
      this.$modal.show('support-ticket-title-edit')
    },
    openRequesterEdit() {
      if (!this.canEditSupport || this.working) return
      this.requesterEdit = this.ticket.requester
      if (this.requesterEdit && !this.requesters.some(user => user.id === this.requesterEdit.id)) {
        this.requesters.unshift(this.requesterEdit)
      }
      this.actionError = ''
      this.$modal.show('support-ticket-requester-edit')
    },
    searchRequesters(search, loading) {
      if (!this.isInternal || !search || search.length < 2) return
      loading(true)
      getSupportTicketUsers({ search, page_size: 30 }).then(response => {
        this.requesters = normalizePage(response.data)
      }).catch(error => {
        this.actionError = apiError(error, 'Unable to search Board users.')
      }).finally(() => loading(false))
    },
    saveTitleEdit() {
      if (!this.canSaveTitleEdit || this.working) return
      return this.saveIdentityEdit(
        { subject: this.titleEdit.trim() },
        'support-ticket-title-edit'
      )
    },
    saveRequesterEdit() {
      if (!this.canSaveRequesterEdit || this.working) return
      return this.saveIdentityEdit(
        { requester: this.requesterEdit.id },
        'support-ticket-requester-edit'
      )
    },
    async saveIdentityEdit(values, modalName) {
      this.working = true
      this.actionError = ''
      try {
        const response = await patchSupportTicket(this.ticket.id, {
          ...values,
          expected_state_version: this.ticket.state_version
        })
        this.ticket = response.data
        this.syncDraft()
        this.$modal.hide(modalName)
        this.updated()
      } catch (error) {
        this.actionError = apiError(error)
        if (isStaleTicketError(error)) this.load()
      } finally {
        this.working = false
      }
    },
    close() { this.$modal.hide(this.modalName) },
    closeSidePanels() { this.detailsOpen = false; this.toolsOpen = false },
    load() {
      this.loading = true
      this.error = ''
      const requests = [getSupportTicket(this.ticketId), getSupportTicketMessages(this.ticketId, { page: 1, page_size: 50 })]
      if (this.isInternal) {
        requests.push(getSupportTicketDictionary('systems', { active: true }))
        requests.push(getSupportTicketDictionary('tags', { active: true }))
        requests.push(getSupportTicketDictionary('responsible_labels', { active: true }))
        requests.push(getSupportTicketUsers({ kind: 'assignee', page_size: 100 }))
        requests.push(getSupportTicketUsers({ page_size: 50 }))
      }
      return Promise.all(requests).then(responses => {
        this.ticket = responses[0].data
        const page = responses[1].data
        this.messages = normalizePage(page).slice().reverse()
        this.messageCount = page.count || this.messages.length
        this.hasOlderMessages = Boolean(page.next)
        this.messagePage = 1
        if (this.isInternal) {
          this.systems = normalizePage(responses[2].data)
          this.tags = normalizePage(responses[3].data)
          this.labels = normalizePage(responses[4].data)
          this.assignees = normalizePage(responses[5].data)
          this.requesters = normalizePage(responses[6].data)
        }
        this.syncDraft()
        this.$nextTick(this.scrollToBottom)
        // Opening a ticket advances this user's read cursor on the backend.
        // Refresh the visible board/list immediately so Unread only and badges
        // reflect the updated read cursor.
        this.$root.$emit('support-ticket-refresh')
      }).catch(error => { this.error = apiError(error) }).finally(() => { this.loading = false })
    },
    syncDraft() {
      this.draft = {
        status: this.ticket.status,
        waiting_for: this.ticket.waiting_for,
        assignee: this.ticket.assignee,
        priority: this.ticket.priority || 'medium',
        is_important: Boolean(this.ticket.is_important),
        system: this.ticket.system,
        direction: this.ticket.direction,
        module: this.ticket.module,
        tags: this.ticket.tags || [],
        responsible_labels: this.ticket.responsible_labels || []
      }
      this.workflowEdit = {
        status: this.ticket.status,
        waiting_for: this.ticket.waiting_for,
        assignee: this.ticket.assignee
      }
      this.classificationEdit = {
        system: this.ticket.system,
        direction: this.ticket.direction,
        module: this.ticket.module,
        priority: this.ticket.priority || 'medium'
      }
      this.titleEdit = this.ticket.subject
      this.requesterEdit = this.ticket.requester
      this.loadDirections(); this.loadModules()
    },
    scrollToBottom() {
      const conversation = this.$refs.conversation
      if (conversation) conversation.scrollToBottom()
    },
    loadDirections() {
      if (!this.isInternal || !this.draft.system) { this.directions = []; return }
      getSupportTicketDictionary('directions', { active: true, system: this.draft.system.id }).then(response => { this.directions = normalizePage(response.data) })
    },
    loadModules() {
      if (!this.isInternal || !this.draft.direction) { this.modules = []; return }
      getSupportTicketDictionary('modules', { active: true, direction: this.draft.direction.id }).then(response => { this.modules = normalizePage(response.data) })
    },
    loadOlderMessages() {
      if (!this.hasOlderMessages || this.loadingMessages) return
      const conversation = this.$refs.conversation
      const previousHeight = conversation ? conversation.scrollHeight() : 0
      this.loadingMessages = true
      getSupportTicketMessages(this.ticketId, { page: this.messagePage + 1, page_size: 50 }).then(response => {
        this.messagePage += 1
        this.messages = [...normalizePage(response.data).slice().reverse(), ...this.messages]
        this.hasOlderMessages = Boolean(response.data.next)
        this.$nextTick(() => { if (conversation) conversation.restoreScrollAfterPrepend(previousHeight) })
      }).finally(() => { this.loadingMessages = false })
    },
    selectMessageFiles(input) {
      const incoming = Array.isArray(input) ? input : Array.from(input.target.files || [])
      const result = appendSupportFiles(this.messageFiles, incoming)
      this.messageFiles = result.files
      this.error = result.error
      if (input.target) input.target.value = ''
    },
    selectNoteFiles(input) {
      const incoming = Array.isArray(input) ? input : Array.from(input.target.files || [])
      const result = appendSupportFiles(this.newNoteFiles, incoming)
      this.newNoteFiles = result.files
      this.error = result.error
      if (input.target) input.target.value = ''
    },
    pasteNoteFiles(event) {
      const files = clipboardSupportFiles(event)
      if (files.length) this.selectNoteFiles(files)
    },
    async sendMessage() {
      if (!this.newMessage?.trim() || this.working) return
      this.working = true
      let uploaded = []
      try {
        uploaded = await uploadSupportFiles(this.messageFiles)
        const response = await postSupportTicketMessage(this.ticket.id, {
          body: this.newMessage.trim(),
          expected_state_version: this.ticket.state_version,
          attachments: uploaded.map(file => file.id)
        }, idempotencyKey())
        this.messages.push(response.data.message)
        this.ticket = { ...this.ticket, ...response.data.ticket }
        this.newMessage = ''
        this.messageFiles = []
        this.messageCount += 1
        this.syncDraft()
        this.$nextTick(this.scrollToBottom)
        this.updated()
      } catch (error) {
        this.error = apiError(error)
        if (isStaleTicketError(error)) this.load()
      } finally {
        this.working = false
      }
    },
    openWorkflowEdit() {
      if (!this.canEditSupport) return
      this.workflowEdit = {
        status: this.ticket.status,
        waiting_for: this.ticket.waiting_for,
        assignee: this.ticket.assignee
      }
      this.actionError = ''
      this.$modal.show('support-ticket-workflow-edit')
    },
    async saveWorkflowEdit() {
      if (!this.canSaveWorkflowEdit || this.working) return
      const requestedStatus = this.workflowEdit.status
      const requestedWaitingFor = this.workflowEdit.waiting_for
      const requestedAssignee = this.workflowEdit.assignee
      const statusChanged = this.workflowStatusChanged
      const metadataChanged = this.workflowMetadataChanged
      this.working = true
      this.actionError = ''
      try {
        if (metadataChanged) {
          this.draft.assignee = requestedAssignee
          const metadataResponse = await patchSupportTicket(this.ticket.id, this.metadataPayload())
          this.ticket = metadataResponse.data
          this.syncDraft()
        }
        if (statusChanged) {
          const workflowResponse = await postSupportTicketTransition(this.ticket.id, {
            status: requestedStatus,
            waiting_for: requestedStatus === 'waiting' ? requestedWaitingFor : null,
            expected_state_version: this.ticket.state_version
          }, idempotencyKey())
          this.ticket = workflowResponse.data
          this.syncDraft()
        }
        this.$modal.hide('support-ticket-workflow-edit')
        this.updated()
      } catch (error) {
        this.actionError = apiError(error)
        if (isStaleTicketError(error)) this.load()
      } finally {
        this.working = false
      }
    },
    openClassificationEdit() {
      if (!this.canEditSupport) return
      this.classificationEdit = {
        system: this.ticket.system,
        direction: this.ticket.direction,
        module: this.ticket.module,
        priority: this.ticket.priority || 'medium'
      }
      this.loadClassificationDirections()
      this.loadClassificationModules()
      this.actionError = ''
      this.$modal.show('support-ticket-classification-edit')
    },
    loadClassificationDirections() {
      if (!this.classificationEdit.system) { this.directions = []; return }
      getSupportTicketDictionary('directions', { active: true, system: this.classificationEdit.system.id }).then(response => { this.directions = normalizePage(response.data) })
    },
    loadClassificationModules() {
      if (!this.classificationEdit.direction) { this.modules = []; return }
      getSupportTicketDictionary('modules', { active: true, direction: this.classificationEdit.direction.id }).then(response => { this.modules = normalizePage(response.data) })
    },
    changeClassificationSystem(system) {
      if (this.working || !system) return
      this.classificationEdit.system = system
      this.classificationEdit.direction = null
      this.classificationEdit.module = null
      this.loadClassificationDirections()
    },
    changeClassificationDirection(direction) {
      if (this.working) return
      this.classificationEdit.direction = direction
      this.classificationEdit.module = null
      this.loadClassificationModules()
    },
    changeClassificationModule(module) { if (!this.working) this.classificationEdit.module = module },
    toggleImportant() {
      if (!this.canEditSupport || this.working) return
      this.saveMetadata({ is_important: !this.ticket.is_important })
    },
    updateTags(tags) { return this.saveMetadataEdit({ tags }) },
    updateResponsibleLabels(responsibleLabels) { return this.saveMetadataEdit({ responsible_labels: responsibleLabels }) },
    dictionaryCreated({ kind, item }) {
      const target = kind === 'tags' ? this.tags : this.labels
      if (!target.some(value => value.id === item.id)) target.push(item)
    },
    dictionaryUpdated({ kind, item }) {
      const replace = values => values.map(value => value.id === item.id ? item : value)
      if (kind === 'tags') {
        this.tags = replace(this.tags)
        this.ticket.tags = replace(this.ticket.tags || [])
        this.draft.tags = replace(this.draft.tags || [])
      } else {
        this.labels = replace(this.labels)
        this.ticket.responsible_labels = replace(this.ticket.responsible_labels || [])
        this.draft.responsible_labels = replace(this.draft.responsible_labels || [])
      }
      this.$root.$emit('support-classifications-updated')
      this.$root.$emit('support-ticket-refresh')
    },
    async saveClassificationEdit() {
      if (!this.classificationChanged || this.working) return
      await this.saveMetadataEdit({
        system: this.classificationEdit.system,
        direction: this.classificationEdit.direction,
        module: this.classificationEdit.module,
        priority: this.classificationEdit.priority
      }, 'support-ticket-classification-edit')
    },
    metadataPayload(extra = {}) {
      return {
        expected_state_version: this.ticket.state_version,
        assignee: this.draft.assignee?.id || null,
        priority: this.draft.priority || 'medium',
        is_important: Boolean(this.draft.is_important),
        system: this.draft.system?.id,
        direction: this.draft.direction?.id || null,
        module: this.draft.module?.id || null,
        tags: this.draft.tags.map(item => item.id),
        responsible_labels: this.draft.responsible_labels.map(item => item.id),
        ...extra
      }
    },
    saveMetadata(extra = {}) {
      if (this.working) return Promise.resolve()
      this.working = true
      return patchSupportTicket(this.ticket.id, this.metadataPayload(extra)).then(response => {
        this.ticket = response.data; this.syncDraft(); this.updated()
      }).catch(error => { this.handleMutationError(error) }).finally(() => { this.working = false })
    },
    async saveMetadataEdit(values, modalName) {
      this.working = true
      this.actionError = ''
      Object.assign(this.draft, values)
      try {
        const response = await patchSupportTicket(this.ticket.id, this.metadataPayload())
        this.ticket = response.data
        this.syncDraft()
        if (modalName) this.$modal.hide(modalName)
        this.updated()
      } catch (error) {
        this.actionError = apiError(error)
        if (isStaleTicketError(error)) this.load()
      } finally {
        this.working = false
      }
    },
    takeIntoWork() {
      if (!this.canTake || this.working) return
      this.working = true
      this.error = ''
      postSupportTicketTake(this.ticket.id, {
        expected_state_version: this.ticket.state_version
      }, idempotencyKey()).then(response => {
        this.ticket = response.data
        this.syncDraft()
        this.updated()
      }).catch(error => { this.handleMutationError(error) }).finally(() => { this.working = false })
    },
    addChecklist() {
      if (!this.canEditSupport) return
      const text = this.newChecklist?.trim(); if (!text || this.working) return
      this.working = true
      postSupportTicketChecklist(this.ticket.id, {
        text,
        expected_state_version: this.ticket.state_version
      }).then(response => {
        this.ticket.checklist_items.push(response.data.item)
        this.applyChecklistTicketState(response.data.ticket)
        this.newChecklist = ''
        this.updated()
      }).catch(error => { this.handleMutationError(error) }).finally(() => { this.working = false })
    },
    toggleChecklist(item, completed) {
      if (!this.canEditSupport) return
      if (this.working) return
      this.working = true
      return patchSupportTicketChecklistItem(item.id, {
        completed,
        expected_state_version: this.ticket.state_version
      }).then(response => {
        Object.assign(item, response.data.item)
        this.applyChecklistTicketState(response.data.ticket)
        this.updated()
      }).catch(error => { this.handleMutationError(error) })
        .finally(() => { this.working = false })
    },
    removeChecklist(item) {
      if (!this.canEditSupport) return
      if (this.working) return
      this.working = true
      return deleteSupportTicketChecklistItem(item.id, {
        expected_state_version: this.ticket.state_version
      }).then(response => {
        this.ticket.checklist_items = this.ticket.checklist_items.filter(value => value.id !== item.id)
        this.applyChecklistTicketState(response.data.ticket)
        this.updated()
      }).catch(error => { this.handleMutationError(error) })
        .finally(() => { this.working = false })
    },
    reorderChecklist() {
      if (!this.canEditSupport) return
      if (this.working) return
      this.working = true
      return postSupportTicketChecklistReorder(
        this.ticket.id,
        this.ticket.checklist_items.map(item => item.id),
        this.ticket.state_version
      ).then(response => {
        this.ticket.checklist_items = response.data.items
        this.applyChecklistTicketState(response.data.ticket)
        this.updated()
      }).catch(error => { this.error = apiError(error); this.load() })
        .finally(() => { this.working = false })
    },
    applyChecklistTicketState(state) {
      this.ticket.state_version = state.state_version
      this.ticket.last_activity_at = state.last_activity_at
    },
    async addNote() {
      if (!this.canEditSupport) return
      const body = this.newNote?.trim(); if (!body || this.working) return
      this.working = true
      let uploaded = []
      try {
        uploaded = await uploadSupportFiles(this.newNoteFiles)
        const response = await postSupportTicketNote(this.ticket.id, {
          body,
          expected_state_version: this.ticket.state_version,
          attachments: uploaded.map(file => file.id)
        }, idempotencyKey())
        this.ticket.notes.push(response.data.note); this.ticket.state_version = response.data.ticket.state_version; this.newNote = ''; this.newNoteFiles = []; this.updated()
      } catch (error) {
        this.handleMutationError(error)
      } finally {
        this.working = false
      }
    },
    deleteAttachment(attachment) {
      if (!attachment?.can_delete || this.working) return
      this.working = true
      deleteSupportTicketAttachment(attachment.id).then(() => {
        this.messages = this.messages.map(message => ({
          ...message,
          attachments: (message.attachments || []).filter(item => item.id !== attachment.id)
        }))
        this.ticket.notes = this.ticket.notes.map(note => ({
          ...note,
          attachments: (note.attachments || []).filter(item => item.id !== attachment.id)
        }))
        this.$notify({ type: 'success', text: 'Attachment removed.' })
      }).catch(error => { this.error = apiError(error) })
        .finally(() => { this.working = false })
    },
    markUnread() { postSupportTicketMarkUnread(this.ticket.id).then(() => { this.updated(); this.close() }).catch(error => { this.error = apiError(error) }) },
    closeTicket() {
      if (!this.canClose) return
      this.actionError = ''
      this.working = true
      postSupportTicketClose(this.ticket.id, {
        resolution: this.closeForm.resolution,
        public_summary: this.closeForm.public_summary,
        internal_reason: this.closeForm.internal_reason,
        duplicate_of: this.closeForm.duplicate_of?.id || null,
        expected_state_version: this.ticket.state_version
      }, idempotencyKey()).then(response => {
        this.ticket = response.data; this.messages.push({ id: `closure-${Date.now()}`, author: this.currentUser, body: this.closeForm.public_summary, attachments: [], created_at: new Date().toISOString() })
        this.closeForm = { resolution: null, public_summary: '', internal_reason: '', duplicate_of: null }
        this.$modal.hide('support-ticket-close'); this.syncDraft(); this.updated()
      }).catch(error => {
        this.actionError = apiError(error)
        if (isStaleTicketError(error)) this.load()
      }).finally(() => { this.working = false })
    },
    reopenTicket() {
      this.actionError = ''
      this.working = true
      postSupportTicketReopen(this.ticket.id, { reason: this.reopenReason, expected_state_version: this.ticket.state_version }, idempotencyKey()).then(response => {
        this.ticket = response.data; this.reopenReason = ''; this.$modal.hide('support-ticket-reopen'); this.syncDraft(); this.load(); this.updated()
      }).catch(error => {
        this.actionError = apiError(error)
        if (isStaleTicketError(error)) this.load()
      }).finally(() => { this.working = false })
    },
    handleMutationError(error) { this.error = apiError(error); if (isStaleTicketError(error)) this.load() },
    updated() { this.timelineRefreshKey += 1; this.$emit('updated', this.ticket); this.$root.$emit('support-ticket-refresh') }
  }
}
</script>

<style lang="scss" scoped>
.ticket-workspace { position: relative; height: 100%; overflow-x: hidden; overflow-y: auto; background: $ui-surface-soft; color: $ui-ink; font-family: $ui-font-body; font-size: 14px; }
.ticket-workspace__loading,
.ticket-workspace__fatal { display: flex; flex: 1; align-items: center; justify-content: center; flex-direction: column; gap: $ui-space-12; padding: $ui-space-24; }
.ticket-workspace__toolbar { display: flex; min-height: 58px; align-items: center; justify-content: space-between; gap: $ui-space-16; padding: 9px 24px; border-bottom: 1px solid $ui-line; background: $ui-surface; }
.ticket-workspace__toolbar > div { display: flex; align-items: center; flex-wrap: wrap; gap: $ui-space-8; }
.ticket-workspace__tag-bar { min-height: 32px; }
.ticket-workspace__take { display: flex; flex: 0 0 auto; align-items: center; justify-content: flex-start; gap: $ui-space-12; padding: $ui-space-12 $ui-space-20; border-bottom: 1px solid rgba($ui-amber, .3); background: linear-gradient(90deg, rgba($ui-amber, .16), rgba($ui-amber, .05)); }
.ticket-workspace__take > div { display: grid; gap: 2px; }
.ticket-workspace__take span { color: #92400e; font-size: $ui-font-size-metadata; font-weight: 700; text-transform: uppercase; }
.ticket-workspace__take strong { color: $ui-ink; font: 600 $ui-font-size-section-title $ui-font-heading; }
.ticket-workspace__take small { color: $ui-muted; }
.ticket-workspace__error { flex: 0 0 auto; margin: 0; padding: $ui-space-8 $ui-space-20; background: rgba($ui-red, .08); color: $ui-red; font-size: $ui-font-size-metadata; }
.ticket-workspace__body { position: relative; display: grid; min-width: 0; grid-template-columns: repeat(2, minmax(0, 1fr)) minmax(420px, 1.15fr); align-items: start; gap: 16px; padding: 20px 24px 32px; background: $ui-surface-soft; }
.ticket-workspace__body--employee { grid-template-columns: minmax(0, 1fr) minmax(280px, 340px); }
.ticket-workspace__body--employee .ticket-conversation { grid-column: 1; }
.ticket-workspace__body--employee .ticket-workspace__details { grid-column: 2; }
.ticket-workspace__operations { display: grid; min-width: 0; grid-column: 1 / 3; grid-row: 1; grid-template-columns: repeat(2, minmax(0, 1fr)); align-items: start; gap: 16px; }
.ticket-workspace__overview { display: grid; min-width: 0; grid-column: 1 / -1; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
.ticket-workspace__tools,
.ticket-workspace__details { display: grid; min-width: 0; align-content: start; gap: 16px; }
.ticket-workspace__tools { display: grid; min-width: 0; grid-column: 1; grid-row: 2; align-content: start; gap: 16px; }
.ticket-workspace__notes { display: grid; min-width: 0; grid-column: 2; grid-row: 2; align-content: start; gap: 16px; }
.ticket-workspace__details { grid-row: 1; grid-column: 2; }
.ticket-conversation { grid-row: 1; grid-column: 3; }
.ticket-workspace__work-panel { min-width: 0; padding: 20px; border: 1px solid $ui-line; border-radius: 16px; background: $ui-surface; box-shadow: 0 8px 26px rgba(28, 45, 74, .055); }
.ticket-workspace__work-panel > header { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.ticket-workspace__work-panel > header strong { color: $ui-ink; font-family: $ui-font-heading; font-size: 16px; font-weight: 600; }
.ticket-workspace__work-panel > header span { color: $ui-muted; font-size: 14px; }
.ticket-workspace__work-content { display: flex; flex-direction: column; gap: 12px; margin-top: 14px; }
.ticket-workspace__work-content--notes > p { margin: 0; color: $ui-muted; font-size: $ui-font-size-metadata; }
.ticket-workspace__work-content--notes .ticket-notes { max-height: none; margin-bottom: 0; }
.ticket-workspace__attachments-panel { padding: 18px 20px; border: 1px solid $ui-line; border-radius: 16px; background: $ui-surface; box-shadow: 0 8px 26px rgba(28, 45, 74, .055); }
.ticket-workspace__attachments-panel > header { display: flex; align-items: center; justify-content: space-between; gap: $ui-space-12; }
.ticket-workspace__attachments-panel > header div { display: grid; gap: 2px; }
.ticket-workspace__attachments-panel > header strong { color: $ui-ink; font: 600 $ui-font-size-section-title $ui-font-heading; }
.ticket-workspace__attachments-panel > header span { color: $ui-muted; font-size: $ui-font-size-metadata; }
.ticket-workspace__attachments-panel .ticket-composer__files { margin-bottom: 0; }
.ticket-workspace__tools-button,
.ticket-workspace__details-button,
.ticket-workspace__panel-header,
.ticket-workspace__scrim { display: none; }
.ticket-workspace__owner-note { margin: $ui-space-8 0 0; color: $ui-muted; font-size: $ui-font-size-metadata; line-height: 1.4; }
.ticket-workspace__summary { display: grid; gap: $ui-space-12; margin: 0; }
.ticket-workspace__summary div { display: flex; justify-content: space-between; gap: $ui-space-12; }
.ticket-workspace__summary dt { color: $ui-muted; }
.ticket-workspace__summary dd { margin: 0; color: $ui-ink; font-weight: 600; text-align: right; }
.ticket-summary-card { min-width: 0; padding: 18px 20px; border: 1px solid $ui-line; border-radius: 16px; background: $ui-surface; box-shadow: 0 8px 26px rgba(28, 45, 74, .055); }
.ticket-summary-card > header { position: relative; }
.ticket-summary-card h3 { margin: 0 0 14px; padding-bottom: 11px; border-bottom: 1px solid $ui-line; color: $ui-ink; font-family: $ui-font-heading; font-size: 16px; font-weight: 600; line-height: 20px; }
.ticket-summary-card > header button { position: absolute; top: -6px; right: 0; display: inline-flex; width: 30px; height: 30px; align-items: center; justify-content: center; padding: 0; border: 0; background: transparent; cursor: pointer; }
.ticket-summary-card > header button img { display: block; width: 30px; height: 30px; object-fit: contain; }
.ticket-summary-card > header button:disabled { cursor: wait; opacity: .55; }
.ticket-summary-card dl { display: grid; gap: 9px; margin: 0; }
.ticket-summary-card dl > div { display: grid; min-width: 0; grid-template-columns: minmax(105px, .85fr) minmax(0, 1.15fr); align-items: start; gap: 10px; }
.ticket-summary-card dt { color: $ui-muted; font-size: 14px; line-height: 20px; }
.ticket-summary-card dd { display: flex; min-width: 0; align-items: center; justify-content: flex-end; gap: $ui-space-8; margin: 0; overflow-wrap: anywhere; color: $ui-ink; font-size: 14px; font-weight: 600; line-height: 20px; text-align: right; }
.ticket-summary-card__badges { flex-wrap: wrap; }
.ticket-summary-card__empty { color: $ui-muted; font-weight: 500; }
.ticket-summary-card__owner img, .ticket-summary-card__owner > span { display: inline-flex; width: 24px; height: 24px; flex: 0 0 24px; align-items: center; justify-content: center; border-radius: 50%; }
.ticket-summary-card__owner img { object-fit: cover; }
.ticket-summary-card__owner > span { background: $ui-surface-soft; color: $ui-blue; font-size: 10px; }
.ticket-summary-card__owner strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ticket-summary-card__important { color: $ui-muted; font-weight: 500; }
.ticket-summary-card__important img { width: 17px; height: 17px; }
.ticket-summary-card__important--active { color: #a16207; font-weight: 600; }
.ticket-summary-card__source-actions { overflow: visible !important; }
.ticket-summary-card__source { display: grid; min-width: 0; max-width: 100%; gap: 1px; text-align: right; }
.ticket-summary-card__source a,
.ticket-summary-card__source small { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ticket-summary-card__source a { color: $ui-blue; font-size: 13px; font-weight: 600; text-decoration: none; }
.ticket-summary-card__source small { max-width: 210px; color: $ui-muted; font-size: 10px; font-weight: 500; }
.ticket-summary-card__source a:hover { text-decoration: underline; }
.ticket-summary-card__source a:focus-visible { border-radius: 6px; outline: 2px solid $ui-blue; outline-offset: 2px; }
.ticket-summary-card__copy-source { display: inline-flex; width: 30px; height: 30px; flex: 0 0 30px; align-items: center; justify-content: center; padding: 0; border: 0; border-radius: 8px; background: transparent; cursor: pointer; }
.ticket-summary-card__copy-source:hover { background: $ui-blue-soft; }
.ticket-summary-card__copy-source:focus-visible { outline: 2px solid $ui-blue; outline-offset: 2px; }
.ticket-summary-card__copy-source img { width: 25px; height: 25px; }
.ticket-notes header { display: flex; justify-content: space-between; gap: $ui-space-12; color: $ui-muted; font-size: $ui-font-size-metadata; }
.ticket-notes header strong { color: $ui-ink; }
.ticket-note__attachments { display: flex; flex-wrap: wrap; gap: $ui-space-8; margin-top: $ui-space-12; }
.ticket-note__attachments a { display: grid; gap: 2px; padding: $ui-space-8 $ui-space-12; border: 1px solid $ui-line-strong; border-radius: $ui-control-radius; background: $ui-surface; color: $ui-text; font-size: $ui-font-size-metadata; text-decoration: none; }
.ticket-note__attach { color: $ui-blue; cursor: pointer; font-size: $ui-font-size-metadata; font-weight: 600; }
.ticket-note__attach input { position: absolute; width: 1px; height: 1px; overflow: hidden; }
.ticket-composer__files { display: flex; flex-wrap: wrap; gap: $ui-space-8; margin-top: $ui-space-8; }
.ticket-composer__files li { padding: $ui-space-4 $ui-space-8; border-radius: $ui-control-radius; background: $ui-surface-soft; font-size: $ui-font-size-metadata; }
.ticket-composer__files button { border: 0; background: transparent; color: $ui-red; cursor: pointer; }
.ticket-notes { display: grid; gap: 9px; margin: 0; }
.ticket-notes article { padding: 10px 11px; border-left: 3px solid $ui-green; border-radius: 0 8px 8px 0; background: $ui-surface-soft; }
.ticket-notes p { margin: 6px 0 0; color: $ui-text; font-size: 14px; line-height: 1.45; white-space: pre-wrap; }
.ticket-note__composer { display: grid; grid-template-columns: minmax(0, 1fr) auto; align-items: end; gap: 8px; }
.ticket-note__composer :deep(.textarea) { min-height: 58px; }
.ticket-note__composer :deep(.app-button) { width: auto; min-width: 0; min-height: $ui-control-height; justify-self: end; padding: 0 16px; border-color: $ui-blue; border-radius: $ui-control-radius; background: $ui-blue; color: #fff; font-size: 13px; font-weight: 600; }
.ticket-action-dialog { background: $ui-page; }
.ticket-action-dialog form { display: grid; padding: $ui-space-20; }
.ticket-action-dialog footer { display: flex; justify-content: flex-end; gap: $ui-space-12; }
.ticket-action-dialog p { color: $ui-red; }
@media (max-width: 1180px) {
  .ticket-workspace__body,
  .ticket-workspace__body--employee { grid-template-columns: minmax(0, 1fr); }
  .ticket-workspace__operations,
  .ticket-conversation,
  .ticket-workspace__details,
  .ticket-workspace__body--employee .ticket-conversation,
  .ticket-workspace__body--employee .ticket-workspace__details { grid-column: 1; grid-row: auto; }
}
@media (max-width: 900px) {
  .ticket-workspace__operations,
  .ticket-workspace__overview { grid-template-columns: minmax(0, 1fr); }
  .ticket-workspace__overview,
  .ticket-workspace__tools,
  .ticket-workspace__notes { grid-column: 1; grid-row: auto; }
}
@media (max-width: 780px) {
  .ticket-workspace__toolbar { align-items: flex-start; flex-direction: column; }
  .ticket-workspace__take { align-items: flex-start; flex-direction: column; }
  .ticket-workspace__body { padding: 16px; }
  .ticket-note__composer { grid-template-columns: minmax(0, 1fr); }
}
</style>
